// Editable content schema for the Quantum Results CMS.
// Labels are page-specific so admins can match each field to what they see on the site.
// Blank field = the code default renders instead. Backend preserves explicit empty
// strings so a cleared field is distinct from "never set".

const seo = [
  { key: "seo_title", label: "SEO — Browser tab title", type: "text" },
  { key: "seo_description", label: "SEO — Meta description (search snippet)", type: "textarea" },
];

const heroFields = [
  { key: "hero_eyebrow", label: "Hero — Eyebrow (small caps above the title)", type: "text" },
  { key: "hero_title", label: "Hero — Title (large headline)", type: "textarea" },
  { key: "hero_lede", label: "Hero — Lede (short intro line under the title)", type: "textarea" },
];

const bodyType = "textarea";

export const CONTENT_SCHEMA = [
  {
    key: "home",
    label: "Home",
    path: "/",
    fields: [
      ...seo,
      { key: "welcome_eyebrow", label: "Welcome — Eyebrow", type: "text" },
      { key: "welcome_p1", label: "Welcome — First paragraph", type: bodyType },
      { key: "welcome_p2", label: "Welcome — Second paragraph", type: bodyType },
      { key: "welcome_cta", label: "Welcome — Primary button label", type: "text" },
      { key: "recognition_eyebrow", label: "Recognition — Eyebrow", type: "text" },
      { key: "recognition_title", label: "Recognition — Title", type: "textarea" },
      { key: "recognition_body", label: "Recognition — Body paragraphs (blank line between paragraphs)", type: bodyType },
      { key: "philosophy_eyebrow", label: "Philosophy — Eyebrow", type: "text" },
      { key: "philosophy_title", label: "Philosophy — Title", type: "textarea" },
      { key: "philosophy_body", label: "Philosophy — Body paragraphs", type: bodyType },
      { key: "reflection_eyebrow", label: "Weekly Reflection — Eyebrow", type: "text" },
      { key: "reflection_body", label: "Weekly Reflection — Body (leave blank to hide the card)", type: bodyType },
      { key: "reflection_attribution", label: "Weekly Reflection — Attribution (optional)", type: "text" },
      { key: "ecosystem_eyebrow", label: "Ecosystem — Eyebrow", type: "text" },
      { key: "ecosystem_anchor", label: "Ecosystem — Italic anchor line", type: "textarea" },
      { key: "ecosystem_intro_body", label: "Ecosystem — Intro paragraphs (above the experience cards)", type: bodyType },
      { key: "meetdan_eyebrow", label: "Meet Dan — Eyebrow", type: "text" },
      { key: "meetdan_title", label: "Meet Dan — Title", type: "textarea" },
      { key: "meetdan_body", label: "Meet Dan — Body paragraphs", type: bodyType },
      { key: "resources_eyebrow", label: "Resources teaser — Eyebrow", type: "text" },
      { key: "resources_title", label: "Resources teaser — Title", type: "textarea" },
      { key: "resources_body", label: "Resources teaser — Body paragraphs", type: bodyType },
      { key: "wherever_eyebrow", label: "Wherever You Are — Eyebrow", type: "text" },
      { key: "wherever_title", label: "Wherever You Are — Title", type: "textarea" },
      { key: "wherever_body", label: "Wherever You Are — Body paragraphs", type: bodyType },
    ],
  },
  {
    key: "about",
    label: "About Dan",
    path: "/about",
    fields: [
      ...seo,
      ...heroFields,
      { key: "belief_body", label: "Belief (opening section) — Body paragraphs", type: bodyType },
      { key: "section2_eyebrow", label: "The Question — Eyebrow", type: "text" },
      { key: "section2_title", label: "The Question — Title", type: "textarea" },
      { key: "section2_body", label: "The Question — Body paragraphs", type: bodyType },
      { key: "section3_eyebrow", label: "Experience — Eyebrow", type: "text" },
      { key: "section3_title", label: "Experience — Title", type: "textarea" },
      { key: "section3_body", label: "Experience — Body paragraphs", type: bodyType },
      { key: "why_eyebrow", label: "Why I Continue — Eyebrow (portrait section)", type: "text" },
      { key: "why_title", label: "Why I Continue — Title", type: "textarea" },
      { key: "why_body", label: "Why I Continue — Body paragraphs", type: bodyType },
      { key: "why_signature", label: "Why I Continue — Signature line (e.g. — Dan Hoover)", type: "text" },
      { key: "closing_note", label: "Closing note above Natural Next Steps", type: "textarea" },
    ],
  },
  {
    key: "four_levels",
    label: "Four Levels",
    path: "/four-levels",
    fields: [
      ...seo,
      ...heroFields,
      { key: "invitation_body", label: "Invitation (opening section) — Body paragraphs", type: bodyType },
      { key: "section2_eyebrow", label: "Recognition — Eyebrow", type: "text" },
      { key: "section2_title", label: "Recognition — Title", type: "textarea" },
      { key: "section2_body", label: "Recognition — Body paragraphs", type: bodyType },
      { key: "section3_eyebrow", label: "A Different Way — Eyebrow", type: "text" },
      { key: "section3_title", label: "A Different Way — Title", type: "textarea" },
      { key: "section3_body", label: "A Different Way — Body paragraphs", type: bodyType },
      { key: "experience_eyebrow", label: "What You'll Experience — Eyebrow (aside-image section)", type: "text" },
      { key: "experience_title", label: "What You'll Experience — Title", type: "textarea" },
      { key: "experience_body", label: "What You'll Experience — Body paragraphs", type: bodyType },
      { key: "section4_eyebrow", label: "Begin the Journey — Eyebrow", type: "text" },
      { key: "section4_title", label: "Begin the Journey — Title", type: "textarea" },
      { key: "section4_body", label: "Begin the Journey — Body paragraphs", type: bodyType },
      { key: "closing_note", label: "Closing note above Natural Next Steps", type: "textarea" },
    ],
  },
  {
    key: "pattern_reveal",
    label: "Pattern Reveal",
    path: "/pattern-reveal",
    fields: [
      ...seo,
      ...heroFields,
      { key: "taking_shape_body", label: "Taking Shape (opening section) — Body paragraphs", type: bodyType },
      { key: "section2_eyebrow", label: "Patterns — Eyebrow", type: "text" },
      { key: "section2_title", label: "Patterns — Title", type: "textarea" },
      { key: "section2_body", label: "Patterns — Body paragraphs", type: bodyType },
      { key: "section3_eyebrow", label: "Recognition — Eyebrow", type: "text" },
      { key: "section3_title", label: "Recognition — Title", type: "textarea" },
      { key: "section3_body", label: "Recognition — Body paragraphs", type: bodyType },
      { key: "section4_eyebrow", label: "Discovery — Eyebrow", type: "text" },
      { key: "section4_title", label: "Discovery — Title", type: "textarea" },
      { key: "section4_body", label: "Discovery — Body paragraphs", type: bodyType },
      { key: "section5_eyebrow", label: "Why I'm Building — Eyebrow", type: "text" },
      { key: "section5_title", label: "Why I'm Building — Title", type: "textarea" },
      { key: "section5_body", label: "Why I'm Building — Body paragraphs", type: bodyType },
      { key: "join_eyebrow", label: "Join the Journey — Eyebrow (waitlist section)", type: "text" },
      { key: "join_title", label: "Join the Journey — Title", type: "textarea" },
      { key: "join_body", label: "Join the Journey — Body paragraphs", type: bodyType },
    ],
  },
  {
    key: "weekly_edge",
    label: "Weekly Quantum Edge",
    path: "/weekly-quantum-edge",
    fields: [
      ...seo,
      ...heroFields,
      { key: "ongoing_body", label: "Opening section — Body paragraphs", type: bodyType },
      { key: "section2_eyebrow", label: "Recognition — Eyebrow", type: "text" },
      { key: "section2_title", label: "Recognition — Title", type: "textarea" },
      { key: "section2_body", label: "Recognition — Body paragraphs", type: bodyType },
      { key: "section3_eyebrow", label: "What You'll Experience — Eyebrow", type: "text" },
      { key: "section3_title", label: "What You'll Experience — Title", type: "textarea" },
      { key: "section3_body", label: "What You'll Experience — Body paragraphs", type: bodyType },
      { key: "section4_eyebrow", label: "Is it right for you — Eyebrow", type: "text" },
      { key: "section4_title", label: "Is it right for you — Title", type: "textarea" },
      { key: "section4_body", label: "Is it right for you — Body paragraphs", type: bodyType },
      { key: "section5_eyebrow", label: "Keep Growing — Eyebrow", type: "text" },
      { key: "section5_title", label: "Keep Growing — Title", type: "textarea" },
      { key: "section5_body", label: "Keep Growing — Body paragraphs", type: bodyType },
    ],
  },
  {
    key: "forward_reset",
    label: "Forward Reset",
    path: "/forward-reset",
    fields: [
      ...seo,
      ...heroFields,
      { key: "intro_body", label: "Opening section — Body paragraphs", type: bodyType },
      { key: "section2_eyebrow", label: "You May Be Here Because — Eyebrow", type: "text" },
      { key: "section2_title", label: "You May Be Here Because — Title", type: "textarea" },
      { key: "section2_body", label: "You May Be Here Because — Body paragraphs", type: bodyType },
      { key: "section3_eyebrow", label: "What We Do Together — Eyebrow", type: "text" },
      { key: "section3_title", label: "What We Do Together — Title", type: "textarea" },
      { key: "section3_body", label: "What We Do Together — Body paragraphs", type: bodyType },
      { key: "section4_eyebrow", label: "Is Forward Reset Right for You — Eyebrow", type: "text" },
      { key: "section4_title", label: "Is Forward Reset Right for You — Title", type: "textarea" },
      { key: "section4_body", label: "Is Forward Reset Right for You — Body paragraphs", type: bodyType },
      { key: "begin_eyebrow", label: "Begin with a Conversation — Eyebrow (Calendly section)", type: "text" },
      { key: "begin_title", label: "Begin with a Conversation — Title", type: "textarea" },
      { key: "begin_body", label: "Begin with a Conversation — Body paragraphs", type: bodyType },
    ],
  },
  {
    key: "daily_edge",
    label: "Daily Quantum Edge",
    path: "/daily-quantum-edge",
    fields: [
      ...seo,
      ...heroFields,
      { key: "intro_body", label: "Opening section — Body paragraphs", type: bodyType },
      { key: "section2_eyebrow", label: "Recognition — Eyebrow", type: "text" },
      { key: "section2_title", label: "Recognition — Title", type: "textarea" },
      { key: "section2_body", label: "Recognition — Body paragraphs", type: bodyType },
      { key: "section3_eyebrow", label: "What You'll Experience — Eyebrow", type: "text" },
      { key: "section3_title", label: "What You'll Experience — Title", type: "textarea" },
      { key: "section3_body", label: "What You'll Experience — Body paragraphs", type: bodyType },
      { key: "section4_eyebrow", label: "Is it right for you — Eyebrow", type: "text" },
      { key: "section4_title", label: "Is it right for you — Title", type: "textarea" },
      { key: "section4_body", label: "Is it right for you — Body paragraphs", type: bodyType },
      { key: "section5_eyebrow", label: "Begin — Eyebrow", type: "text" },
      { key: "section5_title", label: "Begin — Title", type: "textarea" },
      { key: "section5_body", label: "Begin — Body paragraphs", type: bodyType },
    ],
  },
  {
    key: "resources",
    label: "Resources",
    path: "/resources",
    fields: [
      ...seo,
      ...heroFields,
    ],
  },
  {
    key: "contact",
    label: "Contact",
    path: "/contact",
    fields: [
      ...seo,
      ...heroFields,
    ],
  },
  {
    key: "enrollment",
    label: "Enrollment (Four Levels)",
    path: "/four-levels/enroll",
    fields: [
      ...seo,
      { key: "stripe_url", label: "Stripe Payment Link URL (where 'Begin' redirects buyers)", type: "text" },
      { key: "hero_eyebrow", label: "Hero — Eyebrow", type: "text" },
      { key: "hero_title", label: "Hero — Title", type: "textarea" },
      { key: "hero_lede", label: "Hero — Lede", type: "textarea" },
      { key: "awaits_eyebrow", label: "Here's What Awaits You — Eyebrow", type: "text" },
      { key: "awaits_title", label: "Here's What Awaits You — Title", type: "textarea" },
      { key: "awaits_body", label: "Here's What Awaits You — Body paragraphs (blank line between items)", type: bodyType },
      { key: "before_eyebrow", label: "Before You Begin — Eyebrow", type: "text" },
      { key: "before_title", label: "Before You Begin — Title", type: "textarea" },
      { key: "before_body", label: "Before You Begin — Body paragraphs", type: bodyType },
      { key: "personal_eyebrow", label: "Personal Note — Eyebrow", type: "text" },
      { key: "personal_title", label: "Personal Note — Title", type: "textarea" },
      { key: "personal_body", label: "Personal Note — Body paragraphs", type: bodyType },
      { key: "personal_signature", label: "Personal Note — Signature line (e.g. — Dan Hoover)", type: "text" },
      { key: "cta_headline", label: "Payment CTA — Headline", type: "textarea" },
      { key: "cta_body", label: "Payment CTA — Description (price/access line)", type: "textarea" },
      { key: "cta_ready_line", label: "Payment CTA — Italic 'When you're ready…' line", type: "textarea" },
      { key: "cta_button_label", label: "Payment CTA — Button label", type: "text" },
    ],
  },
];

export const SCHEMA_BY_KEY = Object.fromEntries(CONTENT_SCHEMA.map((p) => [p.key, p]));
